/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Data hooks for the geofence screens. The company id is part of every query key, so a company
 * change is a clean fetch. Geofences and categories change only through these screens (or
 * Refresh), so they don't poll; a successful write invalidates them instead.
 */

import { useCallback, useMemo } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { useApi, useCompany } from '@/hooks'
import useLocalStorage from '@/hooks/useLocalStorage'

import { fetchGeofence, fetchGeofenceCategories, fetchGeofencesByBoundaries, fetchManagedGeofences } from './api'
import type { GeofenceBounds } from './types'

export const GEOFENCES_QUERY_KEY = 'managed-geofences'
export const GEOFENCE_QUERY_KEY = 'managed-geofence'
export const GEOFENCE_CATEGORIES_QUERY_KEY = 'managed-geofence-categories'
export const GEOFENCES_BOUNDARIES_QUERY_KEY = 'geofences-by-boundaries'

export const useManagedGeofences = (companyId: number | undefined) => {
  const { api } = useApi()
  return useQuery({
    queryKey: [GEOFENCES_QUERY_KEY, companyId],
    queryFn: () => fetchManagedGeofences(api, companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
  })
}

export const useGeofencesByBoundaries = (
  companyId: number | undefined,
  viewport: GeofenceBounds | undefined,
  enabled: boolean
) => {
  const { api } = useApi()
  return useQuery({
    queryKey: [
      GEOFENCES_BOUNDARIES_QUERY_KEY,
      companyId,
      viewport?.north,
      viewport?.south,
      viewport?.east,
      viewport?.west,
    ],
    queryFn: () => fetchGeofencesByBoundaries(api, companyId!, viewport!),
    enabled: enabled && !!companyId && !!viewport,
    staleTime: 10_000,
    placeholderData: (previousData) => previousData,
  })
}

export const useGeofence = (id: number | undefined, enabled = true) => {
  const { api } = useApi()
  return useQuery({
    queryKey: [GEOFENCE_QUERY_KEY, id],
    queryFn: () => fetchGeofence(api, id!),
    enabled: enabled && !!id,
    // The editor saves the version it loaded, so always start from the latest.
    staleTime: 0,
  })
}

export const useGeofenceCategories = (companyId: number | undefined) => {
  const { api } = useApi()
  return useQuery({
    queryKey: [GEOFENCE_CATEGORIES_QUERY_KEY, companyId],
    queryFn: () => fetchGeofenceCategories(api, companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
  })
}

/** Refetch everything a geofence or category write can change. */
export const useInvalidateGeofences = () => {
  const queryClient = useQueryClient()
  return useCallback(
    () =>
      Promise.all(
        [GEOFENCES_QUERY_KEY, GEOFENCE_QUERY_KEY, GEOFENCE_CATEGORIES_QUERY_KEY].map((key) =>
          queryClient.invalidateQueries({ queryKey: [key] })
        )
      ).then(() => undefined),
    [queryClient]
  )
}

/**
 * The company the geofence screens work on. It lives in `?companyId=` so links keep their scope,
 * falls back to the last one picked on these screens, then to the first top-level company.
 * Returns the id only once it is one the user can see.
 */
export const useGeofenceCompany = (): [number | undefined, (companyId: number | undefined) => void] => {
  const { companies } = useCompany()
  const [searchParams, setSearchParams] = useSearchParams()
  const [remembered, setRemembered] = useLocalStorage<number | null>('geofencesCompanyId', null)

  const companyId = useMemo(() => {
    const accessible = (id: number | null | undefined) => (id && companies.some((c) => c.id === id) ? id : undefined)
    const fromUrl = Number(searchParams.get('companyId'))
    const firstParent =
      companies.find((c) => !c.parentId) ?? companies.find((c) => !companies.some((p) => p.id === c.parentId))
    return accessible(fromUrl) ?? accessible(remembered) ?? firstParent?.id ?? companies.at(0)?.id
  }, [companies, searchParams, remembered])

  const setCompanyId = useCallback(
    (next: number | undefined) => {
      setRemembered(next ?? null)
      setSearchParams(
        (prev) => {
          // A new company has new categories, so its filters start over.
          const params = new URLSearchParams()
          const keep = prev.get('q')
          if (keep) params.set('q', keep)
          if (next) params.set('companyId', String(next))
          return params
        },
        { replace: true }
      )
    },
    [setRemembered, setSearchParams]
  )

  return [companyId, setCompanyId]
}
